import { Controller } from "@hotwired/stimulus";

// **LE TIROIR DU RAIL, SOUS 1024 px** — arbitrage du porteur, 2026-09-13.
//
// Le cahier du prototype veut un tiroir entre 768 et 1023 px : *« rail en tiroir ouvert par un
// bouton, superposition sombre »*. Le back-office s'en passait depuis le lot 6 — le rail
// repassait dans le flux, en bandeau — et le motif écrit était honnête : un tiroir demande un
// état d'ouverture, donc un contrôleur. Le porteur a tranché : **le bandeau coûtait la moitié
// de la hauteur d'écran avant le premier mot de la page.**
//
// ## ⚠️ CE CONTRÔLEUR SORT DE SON ÉLÉMENT, ET C'EST DÉLIBÉRÉ
//
// Il vit sur la BARRE (c'est elle qui porte le bouton) et commute le RAIL, qui est son frère
// dans le layout. Un `outlet` Stimulus aurait demandé un second `data-controller` sur le rail
// pour une seule commande ; une enveloppe commune aurait demandé de toucher le layout des
// quatre espaces. Le chrome est un cas où la commande et sa cible ne partagent pas de boîte.
//
// ## ⛔ CE QU'IL NE FAIT PAS
//
// - **Aucun piège de focus** (`focus trap`) : le tiroir n'est pas une modale, la page derrière
//   reste lisible, et un piège mal posé enferme un lecteur d'écran mieux qu'il ne le guide.
//   Le focus ENTRE dans le rail à l'ouverture et REVIENT au bouton à la fermeture — c'est ce
//   que la navigation au clavier demande, et rien de plus.
// - **Aucun verrou de défilement du corps** : sous 1024 px, le rail tient dans sa hauteur et
//   défile pour lui-même. Bloquer le corps ferait sauter la page en haut à chaque ouverture.
export default class extends Controller {
  static targets = ["button"];
  static values = {
    rail: { type: String, default: "backoffice-rail" },
    scrim: { type: String, default: "backoffice-scrim" },
  };

  connect() {
    // ⚠️ Le rail et sa superposition sont NOMMÉS par la barre qui les ouvre : l'espace parent
    // porte le même tiroir sous d'autres identifiants de test. Les défauts restent ceux du
    // back-office, dont la barre ne déclare rien.
    this.rail = document.querySelector(`[data-testid=${this.railValue}]`);
    // 🔴 **DEUX FORMES DE RAIL COEXISTENT — 2026-09-22.** Les surfaces à rail PLAT (parent,
    // apporteur, studio) rendent `shared/ds/sidebar` : leur superposition est le voile du
    // design system, pas un `<div>` à nous, et elle s'ouvre par une CLASSE, pas par le
    // retrait d'un `hidden`. La console garde son rail jusqu'à sa propre migration.
    //
    // ⚠️ **On DÉTECTE plutôt qu'on ne suppose** : `this.ds` décide des deux gestes ci-dessous.
    // Le jour où la console migre, la branche héritée disparaît d'elle-même.
    this.ds = this.rail?.classList.contains("ds-sidebar") ?? false;
    this.scrim = this.ds
      ? document.querySelector(".ds-sidebar__overlay")
      : document.querySelector(`[data-testid=${this.scrimValue}]`);

    // ⚠️ **Échap ferme, et le clic sur la superposition aussi** : ce sont les deux gestes
    // qu'un tiroir doit accepter. Le clic est écouté sur la superposition elle-même plutôt
    // que sur le document — un écouteur de document fermerait le tiroir au premier clic
    // DANS le rail, c'est-à-dire avant que le lien ne soit suivi.
    this.closeOnEscape = (event) => { if (event.key === "Escape") this.close(); };
    this.closeOnScrim = () => this.close();
    // Une navigation referme : Turbo remplace le corps, l'attribut survivrait au rendu.
    this.closeOnVisit = () => this.close();

    document.addEventListener("keydown", this.closeOnEscape);
    this.scrim?.addEventListener("click", this.closeOnScrim);
    document.addEventListener("turbo:load", this.closeOnVisit);
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape);
    this.scrim?.removeEventListener("click", this.closeOnScrim);
    document.removeEventListener("turbo:load", this.closeOnVisit);
  }

  toggle(event) {
    event?.preventDefault();
    const ouvert = this.ds ? this.rail?.classList.contains("is-open")
                       : this.rail?.hasAttribute("data-open");
    ouvert ? this.close() : this.open();
  }

  open() {
    if (!this.rail) return;

    if (this.ds) {
      this.rail.classList.add("is-open");
      this.scrim?.classList.add("is-open");
    } else {
      this.rail.setAttribute("data-open", "");
      this.scrim?.removeAttribute("hidden");
    }
    this.buttonTarget.setAttribute("aria-expanded", "true");
    // Le premier lien du rail, pour que la tabulation continue là où l'œil est allé.
    this.rail.querySelector("a")?.focus();
  }

  close() {
    if (!this.rail) return;
    // ⚠️ La garde de sortie lit la MÊME marque que l'ouverture pose — sinon un tiroir
    // du DS resterait ouvert, `close()` retournant aussitôt faute de `data-open`.
    const ouvert = this.ds ? this.rail.classList.contains("is-open")
                           : this.rail.hasAttribute("data-open");
    if (!ouvert) return;

    if (this.ds) {
      this.rail.classList.remove("is-open");
      this.scrim?.classList.remove("is-open");
    } else {
      this.rail.removeAttribute("data-open");
      this.scrim?.setAttribute("hidden", "");
    }
    this.buttonTarget.setAttribute("aria-expanded", "false");
    this.buttonTarget.focus();
  }
}
